/**
 * Booked drive sessions (web platform → console handoff).
 *
 * The platform mints a slot-scoped operator JWT and opens the console as
 *   https://relay.example.com/#token=…&until=<epochMs>&return=<portal URL>
 * The token rides the hash so it never reaches any server log; it is parsed
 * once and stripped from the URL. `until` drives the in-cab countdown: a
 * warning at five minutes out, then E-STOP + a handover overlay + redirect
 * back to the portal when the slot ends.
 *
 * This countdown is UX only, NOT the enforcement: the agent schedules its own
 * teardown at the JWT's `exp` (session-manager.ts openSession → expiresAtMs),
 * so stripping `until` or blocking this timer in devtools cannot extend
 * driving — the digger stops and the operator is dropped at `exp` regardless.
 */

export interface DriveSession {
  token: string;
  /** Slot end (ms epoch). Absent for tokens minted without a slot. */
  untilMs?: number;
  /** Where to send the operator when the slot is over. */
  returnUrl?: string;
}

export const WARNING_BEFORE_MS = 5 * 60_000;
const REDIRECT_DELAY_MS = 6_000;

export function parseDriveSession(hash: string): DriveSession | undefined {
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  const token = params.get('token');
  if (!token) return undefined;
  const until = Number(params.get('until'));
  const ret = params.get('return');
  let returnUrl: string | undefined;
  if (ret) {
    try {
      const u = new URL(ret);
      if (u.protocol === 'https:' || u.protocol === 'http:') returnUrl = u.href;
    } catch {
      /* ignore malformed return URLs */
    }
  }
  return {
    token,
    untilMs: Number.isFinite(until) && until > 0 ? until : undefined,
    returnUrl,
  };
}

export interface DriveTimerHooks {
  /** Fired once, five minutes (or fewer, when joining late) before the end. */
  onWarning: (msLeft: number) => void;
  /** Fired once when the slot is over. */
  onEnd: () => void;
  /** Fired every second with the remaining time. */
  onTick?: (msLeft: number) => void;
}

/** Pure timer for the slot lifecycle — injectable clock for tests. */
export class DriveSessionTimer {
  private timers: ReturnType<typeof setTimeout>[] = [];
  private interval: ReturnType<typeof setInterval> | undefined;

  constructor(
    private readonly untilMs: number,
    private readonly hooks: DriveTimerHooks,
    private readonly now: () => number = Date.now,
  ) {}

  start(): void {
    const left = this.untilMs - this.now();
    if (left <= 0) {
      this.hooks.onEnd();
      return;
    }
    if (left <= WARNING_BEFORE_MS) {
      this.hooks.onWarning(left);
    } else {
      this.timers.push(
        setTimeout(() => this.hooks.onWarning(this.untilMs - this.now()), left - WARNING_BEFORE_MS),
      );
    }
    this.timers.push(
      setTimeout(() => {
        this.stop();
        this.hooks.onEnd();
      }, left),
    );
    if (this.hooks.onTick) {
      this.hooks.onTick(left);
      this.interval = setInterval(() => {
        this.hooks.onTick?.(Math.max(0, this.untilMs - this.now()));
      }, 1000);
    }
  }

  stop(): void {
    for (const t of this.timers) clearTimeout(t);
    this.timers = [];
    if (this.interval) clearInterval(this.interval);
    this.interval = undefined;
  }
}

export function formatRemaining(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/* --- DOM layer ------------------------------------------------------------ */

const UI_CSS = `
#drive-clock {
  position: fixed; top: 10px; left: 50%; transform: translateX(-50%);
  z-index: 40; background: rgba(20, 24, 30, 0.85); color: #f5b301;
  font: 700 15px/1 ui-monospace, monospace; padding: 8px 14px;
  border-radius: 999px; border: 1px solid #f5b301; letter-spacing: 0.08em;
}
#drive-clock.warning { background: #d9534f; color: #fff; border-color: #fff; }
#drive-end-overlay {
  position: fixed; inset: 0; z-index: 60; background: rgba(12, 15, 20, 0.96);
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 12px; color: #f6e7c8; text-align: center;
  font: 400 18px/1.5 system-ui, sans-serif;
}
#drive-end-overlay h2 { color: #f5b301; font-size: 32px; margin: 0; }
`;

/**
 * Wires the timer to the console UI: countdown chip, five-minute warning,
 * end-of-slot overlay + E-STOP + redirect back to the portal.
 */
export function startDriveSessionUi(session: DriveSession, estop: () => void): void {
  if (!session.untilMs) return;
  const style = document.createElement('style');
  style.textContent = UI_CSS;
  document.head.appendChild(style);

  const clock = document.createElement('div');
  clock.id = 'drive-clock';
  document.body.appendChild(clock);

  const timer = new DriveSessionTimer(session.untilMs, {
    onTick: (msLeft) => {
      clock.textContent = `⏱ SLOT ${formatRemaining(msLeft)}`;
    },
    onWarning: () => {
      clock.classList.add('warning');
      clock.textContent = '⏱ 5 MINUTES LEFT';
    },
    onEnd: () => {
      estop();
      showEndOverlay(session.returnUrl);
    },
  });
  timer.start();
}

export function showDriveSessionError(message: string, returnUrl?: string): void {
  showEndOverlay(returnUrl, message);
}

function showEndOverlay(returnUrl: string | undefined, message?: string): void {
  let overlay = document.getElementById('drive-end-overlay');
  if (!overlay) {
    overlay = document.createElement('div');
    overlay.id = 'drive-end-overlay';
    document.body.appendChild(overlay);
  }
  overlay.innerHTML = `
    <h2>${message ? '🚧 SESSION ENDED' : "⏰ TIME'S UP!"}</h2>
    <p>${message ?? 'Your slot is complete — the digger has been stopped safely.<br/>Thanks for digging with us!'}</p>
    <p>${returnUrl ? 'Taking you back to your portal…' : 'You can close this tab.'}</p>`;
  if (returnUrl) {
    setTimeout(() => window.location.replace(returnUrl), REDIRECT_DELAY_MS);
  }
}
